import { useUsers } from '@/domains/users';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import type { AssignShiftInput } from '../types/shift';
import {
  MAX_SHIFT_DATE,
  MIN_SHIFT_DATE,
  getShiftDateError,
  getShiftDateRangeError,
} from '../utils/shiftDate';

interface AssignToUserModalProps {
  open: boolean;
  shiftId: number;
  onClose: () => void;
  onAssign: (data: AssignShiftInput) => Promise<void>;
}

export function AssignToUserModal({ open, shiftId, onClose, onAssign }: AssignToUserModalProps) {
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Search users on the server; only fetch while the modal is open.
  const { data: usersData, isLoading } = useUsers(
    { search: search || undefined, limit: '50' },
    open,
  );
  const users = (usersData?.users ?? []).map((user) => ({
    ...user,
    fullName: `${user.firstName ?? ''} ${user.secondName ?? ''}`.trim() || 'Unknown User',
  }));

  const reset = () => {
    setSelectedUserId(null);
    setStartDate('');
    setEndDate('');
    setSearch('');
  };

  const close = () => {
    reset();
    onClose();
  };

  // Chrome's date input accepts a year far outside the supported range and reports
  // it verbatim, so guard the values rather than trusting the picker (SC-6530).
  // Each field is also checked alone so a bad start date is flagged before the end
  // date has been filled in.
  const startDateError = getShiftDateError(startDate);
  const endDateError = getShiftDateError(endDate);
  const dateError = getShiftDateRangeError(startDate, endDate);
  // An out-of-order range implicates both inputs; a per-field error only its own.
  const isOrderingError = dateError !== null && !startDateError && !endDateError;

  const handleSubmit = async () => {
    if (!selectedUserId || !startDate || !endDate || dateError) return;
    setIsSubmitting(true);
    try {
      await onAssign({ shiftId, userId: selectedUserId, startDate, endDate });
      close();
    } catch {
      // Success/error toasts handled globally via the mutation meta.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent className="max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Assign Shift to User</DialogTitle>
          <DialogDescription>Select a user and date range for this shift</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              Select User *
            </label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
              {isLoading ? (
                <div className="text-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-2">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  {search ? 'No users match your search' : 'No users found'}
                </div>
              ) : (
                users.map((user) => (
                  <div
                    key={user.id}
                    className={`p-2 rounded cursor-pointer transition-all ${
                      selectedUserId === user.id
                        ? 'bg-orange-50 dark:bg-orange-950/20 border border-orange-600'
                        : 'hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    <div className="font-medium text-sm text-foreground">{user.fullName}</div>
                    <div className="text-xs text-muted-foreground">
                      {user.jobTitle || 'No job title'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Start Date *
                </label>
                <Input
                  type="date"
                  value={startDate}
                  min={MIN_SHIFT_DATE}
                  max={MAX_SHIFT_DATE}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-invalid={startDateError !== null || isOrderingError}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  End Date *
                </label>
                <Input
                  type="date"
                  value={endDate}
                  min={MIN_SHIFT_DATE}
                  max={MAX_SHIFT_DATE}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-invalid={endDateError !== null || isOrderingError}
                />
              </div>
            </div>
            {dateError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{dateError}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={handleSubmit}
            disabled={
              !selectedUserId || !startDate || !endDate || dateError !== null || isSubmitting
            }
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Assign Shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
